export const SESSION_UTM_SOURCE_EVENT_PROPERTY = 'session_utm_source';
export const SESSION_UTM_MEDIUM_EVENT_PROPERTY = 'session_utm_medium';
export const SESSION_UTM_CAMPAIGN_EVENT_PROPERTY = 'session_utm_campaign';

export type SessionCampaignAttribution = {
  [SESSION_UTM_SOURCE_EVENT_PROPERTY]?: string;
  [SESSION_UTM_MEDIUM_EVENT_PROPERTY]?: string;
  [SESSION_UTM_CAMPAIGN_EVENT_PROPERTY]?: string;
};

export type CampaignCapture = {
  source?: string;
  medium?: string;
  campaign?: string;
};

export type StoredSessionCampaign = {
  sessionId: number;
  attribution: SessionCampaignAttribution;
};

export type SessionCampaignTeardownReason = 'anonymous-handoff' | 'startup-retry' | 'withdrawal';

type SessionCampaignState = {
  pendingCapture?: CampaignCapture;
  active?: StoredSessionCampaign;
};

export type SessionCampaignStorage = {
  clearExcept: (sessionId?: number) => void;
  findLatest: () => SessionCampaignAttribution;
  persist: (record: StoredSessionCampaign) => void;
  restore: (sessionId: number) => SessionCampaignAttribution;
};

type SessionCampaignEvent = {
  canEnrich: boolean;
  capture?: CampaignCapture;
  eventType?: string;
  sessionId?: number;
};

const normalizeValue = (value: string | null | undefined): string | undefined => {
  const normalizedValue = value?.trim();
  return normalizedValue || undefined;
};

export const hasSessionCampaignAttribution = (attribution: SessionCampaignAttribution): boolean =>
  Boolean(
    attribution[SESSION_UTM_SOURCE_EVENT_PROPERTY] ||
      attribution[SESSION_UTM_MEDIUM_EVENT_PROPERTY] ||
      attribution[SESSION_UTM_CAMPAIGN_EVENT_PROPERTY]
  );

const normalizeCapture = ({ source, medium, campaign }: CampaignCapture): CampaignCapture => ({
  source: normalizeValue(source),
  medium: normalizeValue(medium),
  campaign: normalizeValue(campaign),
});

const hasCapture = (capture: CampaignCapture | undefined): capture is CampaignCapture =>
  Boolean(capture?.source || capture?.medium || capture?.campaign);

const toSessionAttribution = ({
  source,
  medium,
  campaign,
}: CampaignCapture): SessionCampaignAttribution => ({
  ...(source && { [SESSION_UTM_SOURCE_EVENT_PROPERTY]: source }),
  ...(medium && { [SESSION_UTM_MEDIUM_EVENT_PROPERTY]: medium }),
  ...(campaign && { [SESSION_UTM_CAMPAIGN_EVENT_PROPERTY]: campaign }),
});

const toCampaignCapture = (
  attribution: SessionCampaignAttribution
): CampaignCapture | undefined => {
  const capture = normalizeCapture({
    source: attribution[SESSION_UTM_SOURCE_EVENT_PROPERTY],
    medium: attribution[SESSION_UTM_MEDIUM_EVENT_PROPERTY],
    campaign: attribution[SESSION_UTM_CAMPAIGN_EVENT_PROPERTY],
  });
  return hasCapture(capture) ? capture : undefined;
};

const isValidSessionId = (sessionId: number | undefined): sessionId is number =>
  sessionId !== undefined && Number.isFinite(sessionId) && sessionId >= 0;

const isSameAttribution = (
  first: SessionCampaignAttribution,
  second: SessionCampaignAttribution
): boolean =>
  first[SESSION_UTM_SOURCE_EVENT_PROPERTY] === second[SESSION_UTM_SOURCE_EVENT_PROPERTY] &&
  first[SESSION_UTM_MEDIUM_EVENT_PROPERTY] === second[SESSION_UTM_MEDIUM_EVENT_PROPERTY] &&
  first[SESSION_UTM_CAMPAIGN_EVENT_PROPERTY] === second[SESSION_UTM_CAMPAIGN_EVENT_PROPERTY];

export class SessionCampaignAttributionState {
  private state: SessionCampaignState = {};

  private queueCapture(capture: CampaignCapture): void {
    const normalizedCapture = normalizeCapture(capture);
    if (!hasCapture(normalizedCapture)) {
      return;
    }

    this.state = {
      ...this.state,
      pendingCapture: normalizedCapture,
    };
  }

  capture(capture: CampaignCapture): void {
    this.queueCapture(capture);
  }

  enrichEvent(
    { canEnrich, capture, eventType, sessionId }: SessionCampaignEvent,
    storage: SessionCampaignStorage
  ): SessionCampaignAttribution {
    if (!canEnrich) {
      return {};
    }

    if (eventType !== 'session_end' && capture) {
      const normalizedCapture = normalizeCapture(capture);
      const { active } = this.state;
      const matchesActiveSession =
        active !== undefined &&
        active.sessionId === sessionId &&
        isSameAttribution(active.attribution, toSessionAttribution(normalizedCapture));
      if (!matchesActiveSession) {
        this.queueCapture(normalizedCapture);
      }
    }

    if (!isValidSessionId(sessionId)) {
      return {};
    }

    if (eventType === 'session_end') {
      return this.state.active?.sessionId === sessionId
        ? this.state.active.attribution
        : storage.restore(sessionId);
    }

    if (this.state.active?.sessionId !== sessionId) {
      storage.clearExcept(sessionId);
      const attribution = this.state.pendingCapture
        ? toSessionAttribution(this.state.pendingCapture)
        : storage.restore(sessionId);
      const active = { sessionId, attribution };
      this.state = { active };

      if (hasSessionCampaignAttribution(attribution)) {
        storage.persist(active);
      }

      return attribution;
    }

    if (this.state.pendingCapture) {
      const attribution = toSessionAttribution(this.state.pendingCapture);
      const active = { sessionId, attribution };
      this.state = { active };
      storage.persist(active);
      return attribution;
    }

    return this.state.active.attribution;
  }

  teardown(reason: SessionCampaignTeardownReason, storage: SessionCampaignStorage): void {
    if (reason === 'startup-retry') {
      const pendingCapture =
        this.state.pendingCapture ??
        (this.state.active ? toCampaignCapture(this.state.active.attribution) : undefined) ??
        toCampaignCapture(storage.findLatest());
      this.state = pendingCapture ? { pendingCapture } : {};
      return;
    }

    storage.clearExcept();
    this.state =
      reason === 'anonymous-handoff' && this.state.pendingCapture
        ? { pendingCapture: this.state.pendingCapture }
        : {};
  }
}
